// ============================================================
//  src/auth.js — Lógica de autenticación con Firebase Auth
// ============================================================

import {
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile,
    sendPasswordResetEmail
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';

import { auth, functions, db } from './firebase.js';
import { mostrarToast, mostrarVista, toggleBoton } from './ui.js';
import { iniciarEscuchaEdiciones, detenerEscuchaEdiciones } from './firestore.js';
import { guardarIdiomaEnPerfil, guardarPerfil } from './perfil-datos.js';
import { guardarAccessToken, limpiarAccessToken, tokenDriveEsValido, registrarRenovadorToken } from './drive.js';
import { cargarYAplicarPerfil, setPerfilActual, cerrarPanelPerfil, sincronizarPerfilActualDesdeFirestore } from './perfil.js';
import { inicializarIdioma, idiomaActual, bootstrapFueExplicito, t } from './i18n.js';
import { manejarSubmitSolicitud } from './solicitud.js';
import { iniciarEscuchaNotificaciones,
         detenerEscuchaNotificaciones,
         iniciarEscuchaAdminPendientes,
         detenerEscuchaAdminPendientes } from './notifications.js';
import { abrirVentanaOAuthDrive, DRIVE_PENDING_CODE_KEY } from './oauth-drive.js';
import { esAdmin, aprobarSolicitud, rechazarSolicitud, iniciarEscuchaAdmin, cerrarPanelAdmin } from './admin.js';
import { initOnboarding } from './onboarding.js';

// Proveedor para login normal (sin scope Drive)
const proveedorGoogle = new GoogleAuthProvider();
proveedorGoogle.setCustomParameters({ prompt: 'select_account' });

// Evitar listeners duplicados
let listenersAdminConectados = false;
let listenerSolicitudConectado = false;
let listenersRevisionConectados = false;

// ============================================================
//  Registro con email y contraseña
// ============================================================

export async function registrarConEmail(nombre, email, contrasena) {
    toggleBoton('btn-registrar', false, t('login.btn.registrando'));
    try {
        const resultado = await createUserWithEmailAndPassword(auth, email, contrasena);
        await updateProfile(resultado.user, { displayName: nombre });
    } catch (error) {
        console.error('[Auth] Error al registrar:', error);
        mostrarToast(obtenerMensajeError(error.code), 'error');
        toggleBoton('btn-registrar', true);
    }
}

// ============================================================
//  Login con email y contraseña
// ============================================================

export async function iniciarSesionConEmail(email, contrasena) {
    toggleBoton('btn-login-email', false, t('login.btn.conectando'));
    try {
        await signInWithEmailAndPassword(auth, email, contrasena);
    } catch (error) {
        console.error('[Auth] Error al iniciar sesión con email:', error);
        mostrarToast(obtenerMensajeError(error.code), 'error');
        toggleBoton('btn-login-email', true);
    }
}

// ============================================================
//  Recuperar contraseña
// ============================================================

export async function recuperarContrasena(email) {
    if (!email) {
        mostrarToast(t('auth.recuperar.email-requerido'), 'error');
        return;
    }
    try {
        await sendPasswordResetEmail(auth, email);
        mostrarToast(t('auth.recuperar.enviado'), 'exito');
    } catch (error) {
        console.error('[Auth] Error al recuperar contraseña:', error);
        mostrarToast(obtenerMensajeError(error.code), 'error');
    }
}

// ============================================================
//  Inicio de sesión normal (Google)
// ============================================================

export async function iniciarSesionConGoogle() {
    toggleBoton('btn-login', false, t('login.btn.conectando'));

    try {
        await signInWithPopup(auth, proveedorGoogle);
    } catch (error) {
        console.error('[Auth] Error al iniciar sesión:', error);
        mostrarToast(obtenerMensajeError(error.code), 'error');
        toggleBoton('btn-login', true);
    }
}

// ============================================================
//  Autorización de Google Drive
//
//  Flujo completo:
//    1. Abre ventana OAuth → el usuario autoriza en Google
//    2. oauth-callback.html recibe el redirect y envía el code
//       de vuelta vía postMessage
//    3. Llama a la Cloud Function driveAuthorize con ese code
//    4. La función lo intercambia por access_token + refresh_token
//       y persiste el refresh_token en Firestore
//    5. Guarda el access_token en memoria para uso inmediato
//
//  Retorna:
//    true  — Drive autorizado con access_token válido
//    false — cancelado, bloqueado o error
// ============================================================

export async function solicitarPermisosDrive() {
    // Si ya hay token válido en memoria, no es necesario volver a autorizar
    if (tokenDriveEsValido()) return true;

    // ── Paso 1: obtener authorization code via ventana OAuth ──
    let code;
    try {
        code = await abrirVentanaOAuthDrive();
    } catch (error) {
        // Cancelación o bloqueo de popup — no mostrar toast
        const SILENCIOSOS = ['popup-closed', 'popup-blocked', 'oauth-cancelled'];
        if (!SILENCIOSOS.includes(error.message)) {
            mostrarToast(error.message, 'error');
        }
        return false;
    }

    // ── Paso 2: intercambiar código por tokens vía Cloud Function ──
    try {
        const driveAuthorizeFn = httpsCallable(functions, 'driveAuthorize');
        const resultado = await driveAuthorizeFn({ code });

        const access_token = resultado?.data?.access_token;
        if (!access_token) {
            throw new Error('La función no devolvió access_token.');
        }

        guardarAccessToken(access_token);
        sessionStorage.removeItem(DRIVE_PENDING_CODE_KEY);
        console.log('[Auth] Drive autorizado correctamente vía Cloud Function.');
        return true;

    } catch (error) {
        console.error('[Auth] Error en driveAuthorize:', error);
        sessionStorage.removeItem(DRIVE_PENDING_CODE_KEY);
        // error.message viene de HttpsError lanzado en la Cloud Function
        mostrarToast(error?.message ?? t('auth.error.generico'), 'error');
        return false;
    }
}

// ============================================================
//  Renovar access token de Drive (silencioso, sin popup)
//
//  Llama a la Cloud Function driveRefresh, que usa el
//  refresh_token guardado en Firestore para obtener un nuevo
//  access_token de Google.
//
//  Retorna:
//    true  — token renovado y guardado en memoria
//    false — error (el refresh_token puede haber sido revocado)
// ============================================================

export async function renovarAccessToken() {
    try {
        const driveRefreshFn = httpsCallable(functions, 'driveRefresh');
        const resultado = await driveRefreshFn({});

        const access_token = resultado?.data?.access_token;
        if (!access_token) {
            throw new Error('driveRefresh no devolvió access_token.');
        }

        guardarAccessToken(access_token);
        console.log('[Auth] Access token de Drive renovado silenciosamente.');
        return true;

    } catch (error) {
        console.error('[Auth] Error al renovar access token de Drive:', error);
        limpiarAccessToken();
        return false;
    }
}

// Registrar el renovador en drive.js para que pueda hacer retry en 401
// sin crear dependencia circular (drive.js no importa auth.js)
registrarRenovadorToken(renovarAccessToken);

// ============================================================
//  Cierre de sesión
// ============================================================

export async function cerrarSesion(opciones = {}) {
    const { silencioso = false } = opciones;

    try {
        detenerEscuchaEdiciones();
        limpiarAccessToken();
        await signOut(auth);

        if (!silencioso) {
            mostrarToast(t('auth.sesion.cerrada'), 'info');
        }

    } catch (error) {
        console.error('[Auth] Error al cerrar sesión:', error);

        if (!silencioso) {
            mostrarToast(t('auth.sesion.error-cerrar'), 'error');
        }
    }
}

// ============================================================
//  Observador de autenticación
// ============================================================

// Guards against onAuthStateChanged firing multiple times for the same UID.
// Firebase can fire the callback twice: once from the IndexedDB session cache
// and again after a popup/token refresh completes (e.g. stale __/auth/handler
// popup from a previous session). Only the first call per UID runs full
// profile+language hydration.
let _uidHidratado = null;

let _uidEscuchaPerfilUsuario = null;
let _cancelarEscuchaPerfilUsuario = null;

function iniciarEscuchaPerfilUsuario(uid) {
    if (_uidEscuchaPerfilUsuario === uid && _cancelarEscuchaPerfilUsuario) return;

    detenerEscuchaPerfilUsuario();

    _uidEscuchaPerfilUsuario = uid;
    _cancelarEscuchaPerfilUsuario = onSnapshot(
        doc(db, 'users', uid),
        (snapshot) => {
            if (!snapshot.exists()) return;
            sincronizarPerfilActualDesdeFirestore(snapshot.data());
        },
        (error) => {
            console.error('[Auth] Error en escucha realtime del perfil:', error);
        }
    );
}

function detenerEscuchaPerfilUsuario() {
    if (_cancelarEscuchaPerfilUsuario) {
        _cancelarEscuchaPerfilUsuario();
        _cancelarEscuchaPerfilUsuario = null;
    }
    _uidEscuchaPerfilUsuario = null;
}

export function iniciarObservadorAuth() {
    onAuthStateChanged(auth, async (usuario) => {
        if (usuario) {
            // Retorno post-login desde páginas que delegan el login en /app
            // (solo rutas internas en lista blanca).
            const _next = new URLSearchParams(location.search).get('next');
            if (_next === '/analizador-cabrillo') {
                location.replace(_next);
                return;
            }
            // Always update display name / Google photo — this is cheap and safe.
            actualizarUIUsuario(usuario);
            mostrarVista('vista-dashboard');
            const _guiaParam = new URLSearchParams(location.search).get('guia');
            if (_guiaParam === '1') history.replaceState({}, '', location.pathname);
            initOnboarding(_guiaParam === '1' ? { forzar: true } : {});
            conectarListenersUnaSolaVez();
            configurarAdminSiCorresponde(usuario);
            await usuario.getIdToken();
            iniciarEscuchaEdiciones(usuario.uid);
            iniciarEscuchaNotificaciones(usuario.uid);
            iniciarEscuchaPerfilUsuario(usuario.uid);

            // Skip full hydration if already done for this UID this session.
            if (_uidHidratado === usuario.uid) return;
            _uidHidratado = usuario.uid;

            console.log('[Auth] Usuario activo:', usuario.email);

            try {
                const perfil = await cargarYAplicarPerfil(usuario);

                setPerfilActual(perfil);

                const idiomaFirestore = perfil?.preferencias?.idioma ?? perfil?.idioma ?? null;

                // Migrate legacy top-level idioma to preferencias.idioma
                if (perfil?.idioma && !perfil?.preferencias?.idioma) {
                    guardarIdiomaEnPerfil(usuario.uid, perfil.idioma).catch(() => {});
                }

                if (bootstrapFueExplicito()) {
                    // Bootstrap resolved a language from URL param, home selection, or
                    // localStorage — keep it as-is and sync it to the profile if needed.
                    const idiomaActivo = idiomaActual();
                    if (idiomaActivo !== idiomaFirestore) {
                        guardarIdiomaEnPerfil(usuario.uid, idiomaActivo).catch(() => {});
                    }
                } else {
                    // Bootstrap fell back to 'es' default — Firestore is authoritative.
                    inicializarIdioma(idiomaFirestore);
                    const idiomaFinal = idiomaActual();
                    if (idiomaFinal !== idiomaFirestore) {
                        guardarIdiomaEnPerfil(usuario.uid, idiomaFinal).catch(() => {});
                    }
                }

                if (perfil?.driveAutorizado === true) {
                    // Drive ya fue autorizado. Si no hay token válido en memoria/localStorage
                    // (recarga, token expirado) intentar renovarlo silenciosamente sin popup.
                    if (!tokenDriveEsValido()) {
                        const renovado = await renovarAccessToken();
                        if (!renovado) {
                            // El refresh_token fue revocado o no existe — resetear estado.
                            guardarPerfil(usuario.uid, { driveAutorizado: false }).catch(() => {});
                        }
                    }
                } else {
                    limpiarAccessToken();
                }

            } catch (error) {
                console.error('[Auth] Error al cargar perfil:', error);
            }

        } else {
            _uidHidratado = null;
            console.log('[Auth] Sin sesión activa.');
            detenerEscuchaEdiciones();
            detenerEscuchaNotificaciones();
            detenerEscuchaPerfilUsuario();
            limpiarAccessToken();
            setPerfilActual(null);
            cerrarPanelPerfil();
            cerrarPanelAdmin();
            detenerEscuchaAdminPendientes();
            mostrarVista('vista-login');
            toggleBoton('btn-login', true);
            toggleBoton('btn-login-email', true);
            toggleBoton('btn-registrar', true);
            ocultarBotonAdmin();
        }
    });
}

// ============================================================
//  Conexión de listeners únicos
// ============================================================

function conectarListenersUnaSolaVez() {
    conectarListenerSolicitud();
    conectarListenersRevision();
    conectarListenersAdminOverlay();
}

function conectarListenerSolicitud() {
    if (listenerSolicitudConectado) return;

    const formSolicitud = document.getElementById('form-solicitud');
    if (formSolicitud) {
        formSolicitud.addEventListener('submit', manejarSubmitSolicitud);
        listenerSolicitudConectado = true;
    }
}

function conectarListenersRevision() {
    if (listenersRevisionConectados) return;

    const btnCerrarRevision = document.getElementById('btn-cerrar-revision');
    if (btnCerrarRevision) {
        btnCerrarRevision.addEventListener('click', () => {
            document.getElementById('modal-revision')?.classList.remove('modal--visible');
        });
    }

    const btnAprobar = document.getElementById('btn-aprobar');
    if (btnAprobar) {
        btnAprobar.addEventListener('click', () => {
            aprobarSolicitud();
        });
    }

    const btnRechazar = document.getElementById('btn-rechazar');
    if (btnRechazar) {
        btnRechazar.addEventListener('click', () => {
            rechazarSolicitud();
        });
    }

    listenersRevisionConectados = true;
}

function conectarListenersAdminOverlay() {
    if (listenersAdminConectados) return;

    const btnCerrarAdmin = document.getElementById('btn-cerrar-admin');
    if (btnCerrarAdmin) {
        btnCerrarAdmin.addEventListener('click', () => {
            document.getElementById('panel-admin-overlay')?.classList.remove('modal--visible');
        });
    }

    const overlay = document.getElementById('panel-admin-overlay');
    if (overlay) {
        overlay.addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                overlay.classList.remove('modal--visible');
            }
        });
    }

    listenersAdminConectados = true;
}

// ============================================================
//  Admin
// ============================================================

function configurarAdminSiCorresponde(usuario) {
    const btnAdmin = document.getElementById('btn-abrir-admin');
    if (!btnAdmin) return;

    const seccionAdmin = document.getElementById('perfil-seccion-admin');
    const btnAdminConfig = document.getElementById('btn-abrir-admin-config');

    if (esAdmin()) {
        btnAdmin.style.display = 'flex';

        btnAdmin.onclick = () => { window.location.href = '/admin'; };

        if (seccionAdmin) seccionAdmin.style.display = '';
        if (btnAdminConfig) btnAdminConfig.onclick = () => { window.location.href = '/admin'; };

        iniciarEscuchaAdminPendientes();

    } else {
        btnAdmin.style.display = 'none';
        btnAdmin.onclick = null;
        if (seccionAdmin) seccionAdmin.style.display = 'none';
        detenerEscuchaAdminPendientes();
    }
}

function ocultarBotonAdmin() {
    const btnAdmin = document.getElementById('btn-abrir-admin');
    if (!btnAdmin) return;
    btnAdmin.style.display = 'none';
    btnAdmin.onclick = null;
}

// ============================================================
//  Helpers UI
// ============================================================

function actualizarUIUsuario(usuario) {
    const elNombre       = document.getElementById('usuario-nombre');
    const elEmail        = document.getElementById('usuario-email');
    const elAvatar       = document.getElementById('usuario-avatar');
    const elNombreNavbar = document.getElementById('navbar-nombre-usuario');

    const nombreCompleto = usuario.displayName ?? 'Usuario';

    if (elNombre)       elNombre.textContent = nombreCompleto;
    if (elEmail)        elEmail.textContent = usuario.email ?? '';
    if (elNombreNavbar) elNombreNavbar.textContent = nombreCompleto;

    if (elAvatar && usuario.photoURL) {
        elAvatar.referrerPolicy = 'no-referrer';
        elAvatar.src = usuario.photoURL;
        elAvatar.alt = nombreCompleto;
    }
}

function obtenerMensajeError(codigo) {
    const mapa = {
        'auth/popup-closed-by-user':    'auth.error.popup-cerrado',
        'auth/popup-blocked':           'auth.error.popup-bloqueado',
        'auth/cancelled-popup-request': 'auth.error.popup-cancelado',
        'auth/network-request-failed':  'auth.error.red',
        'auth/too-many-requests':       'auth.error.muchos-intentos',
        'auth/email-already-in-use':    'auth.error.email-en-uso',
        'auth/invalid-email':           'auth.error.email-invalido',
        'auth/weak-password':           'auth.error.contrasena-debil',
        'auth/user-not-found':          'auth.error.usuario-no-encontrado',
        'auth/wrong-password':          'auth.error.contrasena-incorrecta',
        'auth/invalid-credential':      'auth.error.credencial-invalida',
    };

    const clave = mapa[codigo];
    if (clave) return t(clave);
    return `${t('auth.error.generico')} ${codigo}`;
}
